import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Button,
  Typography,
  Tooltip,
} from "antd";
import ReactApexChart from "react-apexcharts";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarryOutOutlined,
  UnorderedListOutlined,
  ExpandOutlined,
  FullscreenExitOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useGetTaskDistributionQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";
import dayjs from "dayjs";

const { Title, Text } = Typography;

// Colors matching your dashboard style
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
  danger: "#dc2626",
};

export default function TaskDistribution({ fromDate, toDate }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    data: distribution,
    isLoading,
    error,
  } = useGetTaskDistributionQuery({ from: fromDate && fromDate.format("YYYY-MM-DDTHH:mm:ss"), to: toDate && toDate.format("YYYY-MM-DDTHH:mm:ss") },{refetchOnMountOrArgChange: true});

  if (isLoading) return <Loader />;

  if (error)
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Alert
          message={trans("Failed to load visit distribution")}
          type="error"
          showIcon
        />
      </Card>
    );

  const {
    byPriority = [],
    topLocations = [],
    byType = [],
    byStatus = [],
  } = distribution || {};
  const totalTasks = byStatus.reduce((sum, item) => sum + item.count, 0);

  const completedTasks = byStatus.find((s) => s.status === "done")?.count || 0;
  const inProgressTasks =
    byStatus.find((s) => s.status === "inProgress")?.count || 0;
  const todoTasks = byStatus.find((s) => s.status === "todo")?.count || 0;

  // Status cards configuration
  const statusCards = [
    {
      title: trans("Total Visits"),
      icon: (
        <img
          src={`${process.env.PUBLIC_URL}/assets/totalTasks.svg`}
          alt="Total Visits"
          style={{ width: 45 }}
        />
      ),
      count: totalTasks,
      color: colorScheme.info,
      tooltip: trans("Overall count of visits"),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("Completed"),
      icon: (
        <img
          src={`${process.env.PUBLIC_URL}/assets/complete.svg`}
          alt="Completed"
          style={{ width: 45 }}
        />
      ),
      count: completedTasks,
      color: colorScheme.success,
      tooltip: trans("Number of tasks successfully completed"),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        status: "done",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("In Progress"),
      icon: (
        <img
          src={`${process.env.PUBLIC_URL}/assets/inProgress.svg`}
          alt="In Progress"
          style={{ width: 45 }}
        />
      ),
      count: inProgressTasks,
      color: colorScheme.warning,
      tooltip: trans("Tasks currently being worked on"),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff")  ,
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        status: "inProgress",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("ToDo"),
      icon: (
        <img
          src={`${process.env.PUBLIC_URL}/assets/pending.svg`}
          alt="Pending"
          style={{ width: 45 }}
        />
      ),
      count: todoTasks,
      tooltip: trans("Tasks awaiting action or start"),
      color:
        todoTasks > totalTasks * 0.3 ? colorScheme.danger : colorScheme.warning,
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        status: "todo,unAssigned",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
  ];

  // Alert-style cards for priorities
  const priorityAlerts = byPriority.map((item) => {
    const priorityConfig = {
      urgent: {
        color: colorScheme.danger,
        icon: WarningOutlined,
        label: "Urgent Tasks",
      },
      high: {
        color: colorScheme.warning,
        icon: FlagOutlined,
        label: "High_Priority",
      },
      medium: {
        color: colorScheme.info,
        icon: ClockCircleOutlined,
        label: trans("Medium_Priority"),
      },
      low: {
        color: colorScheme.success,
        icon: CheckCircleOutlined,
        label: trans("Low_Priority"),
      },
    };

    const config = priorityConfig[item.priority] || {
      color: colorScheme.neutral,
      icon: UnorderedListOutlined,
      label: "Other",
    };

    return {
      ...item,
      ...config,
      count: item.count,
    };
  });

  // Chart data
  const locationData = topLocations.slice(0, 6).map((item, index) => ({
    name: item.location
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase()),
    count: item.count,
  }));

  const typeData = byType.slice(0, 5).map((item, index) => ({
    name: item.type || "Other",
    count: item.count,
  }));

  // Chart configurations
  const pieOptions = {
    chart: {
      type: "donut",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
    },
    colors: [
      colorScheme.primary,
      colorScheme.info,
      colorScheme.warning,
      colorScheme.success,
      "#8b5cf6",
      "#ec4899",
    ],
    labels: locationData.map((item) => item.name),
    legend: {
      position: "bottom",
      fontSize: "12px",
      labels: { colors: colorScheme.dark },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: false,
          },
        },
      },
    },
    dataLabels: { enabled: false }, 
    stroke: { width: 2, colors: [colorScheme.surface] },
    tooltip: {
      enabled: true, 
      y: {
        formatter: function (val, opts) {
          return val + " visits";
        },
      },
    },
  };

  const barOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
    },
    colors: ["#6200EE"],
    plotOptions: {
      bar: {
        borderRadius: 7,
        borderRadiusApplication: "end",
        horizontal: false,
        columnWidth: "40%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: typeData.map((item) => item.name),
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "10px" },
      },
    },
    yaxis: {
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "10px" },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return val + " visits";
        },
      },
    },
  };

  return (
    <div
      style={{
        // background: colorScheme.light,
        padding: "16px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,
      }}
    >
      <SectionTitle
        title={trans("Visit Distribution")}
        subtitle={trans("Comprehensive Visit Analytics")}
        imgSrc={`${process.env.PUBLIC_URL}/assets/dashboard-square.svg`}
        imgBgColor="#FFD8BF"
        filterApplied={fromDate && toDate ? true : false}
        filterText={`From ${dayjs(fromDate).format("DD/MM/YYYY")} - ${dayjs(toDate).format("DD/MM/YYYY")}`}
      />

      {/* Status Cards - Updated font styling */}
      <Row gutter={[12, 12]} style={{ marginBottom: "20px" }}>
        {statusCards.map((card, i) => (
          <Col span={6} key={i}>
            <NumbersCard
              title={card.title}
              value={card.count}
              suffix={card.suffix}
              color={card.color}
              tooltip={card.tooltip}
              onClick={() => card.api && setSelectedAlert(card)}
              popup={card.api ? true : false}
            />
          </Col>
        ))}
      </Row>

      {/* Priority Breakdown - Second Row */}

      {/* Charts Row */}
      <Row gutter={[16, 16]} align="stretch">
        {/* Location Distribution */}
        <Col span={isExpanded ? 12 : 24}>
          <Tooltip
            title={trans("Shows how data is distributed across locations")}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
              }}
              bodyStyle={{ padding: "16px", height: "100%" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {trans("Distribution by Location")}
                  </span>
                </div>
              }
            >
              {locationData.length > 0 ? (
                <ReactApexChart
                  options={pieOptions}
                  series={locationData.map((item) => item.count)}
                  type="donut"
                  height={300}
                />
              ) : (
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colorScheme.neutral,
                  }}
                >
                  {trans("No location data available")}
                </div>
              )}
            </Card>
          </Tooltip>
        </Col>

        {/* Task Types */}
        <Col span={isExpanded ? 12 : 24}>
          <Tooltip title={trans("Displays task distribution based on type")}>
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
              }}
              bodyStyle={{ padding: "16px", height: "100%" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {trans("Tasks by Type")}
                  </span>
                </div>
              }
            >
              {typeData.length > 0 ? (
                <ReactApexChart
                  options={barOptions}
                  series={[
                    {
                      name: "Tasks",
                      data: typeData.map((item) => item.count),
                    },
                  ]}
                  type="bar"
                  height={300}
                />
              ) : (
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colorScheme.neutral,
                  }}
                >
                  {trans("No task type data available")}
                </div>
              )}
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Expanded View - Priority Breakdown */}
      {isExpanded && (
        <Row style={{ marginTop: "16px" }}>
          <Col span={24}>
            <Tooltip
              title={trans(
                "Displays task distribution by priority level to help identify workload "
              )}
            >
              <Card
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "16px" }}
                title={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600" }}>
                      {trans("Priority Breakdown")}
                    </span>
                  </div>
                }
              >
                <Row gutter={[12, 12]}>
                  {byPriority.map((item, index) => {
                    const priorityConfig = {
                      urgent: { color: colorScheme.danger, label: "Urgent" },
  high: {
    color: colorScheme.warning,
    label: trans("High_Priority"),
  },
  medium: {
    color: colorScheme.info,
    label: trans("Medium_Priority"),
  },
  low: {
    color: colorScheme.success,
    label: trans("Low_Priority"),
  },
                    };

                    const config = priorityConfig[item.priority] || {
                      color: colorScheme.neutral,
                      label: "Other",
                    };

                    return (
                      <Col span={8} key={index}>
                        <div
                          style={{
                            padding: "8px",
                            background:
                              item.priority === "high"
                                ? "#FFF1F088" // High
                                : item.priority === "medium"
                                ? "#FFD8BF88" // Medium
                                : item.priority === "low"
                                ? "#EAF4EC88" // Low
                                : `linear-gradient(135deg, ${colorScheme.surface} 0%, ${colorScheme.light} 100%)`,
                            borderRadius: "16px",
                            border: `1px solid #EBECF0`,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          bodyStyle={{ textAlign: "center" }}
                        >
                          <Text
                            style={{
                              fontSize: "14px",
                              color: "#000000",
                              display: "block",
                              marginBottom: "8px",
                              textTransform: "uppercase",
                              fontWeight: "500",
                            }}
                          >
                            {config.label}
                          </Text>
                          <div>
                            <Text
                              style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#000000",
                              }}
                            >
                              {item.count}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </Tooltip>
          </Col>
        </Row>
      )}
      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}
    </div>
  );
}
